import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MasterDrawer from '../Master/create/MasterDrawer';
import { useGetBrandsQuery, useGetModelsByBrandQuery } from '../../api/master/MasterApi';
import {
  useCheckProductDuplicateMutation,
  useLazyFindProductByBarcodeQuery,
  useSaveProductMutation,
  useUpdateProductMutation,
} from '../../api/product/ProductApi';
import { Product, ProductSpec } from '../../types/product';
import { useSnackbar } from '../../utils/snackbar';

const UOMS = ['PCS', 'NOS', 'SET', 'BOX', 'PKT'];
const COLOURS = ['Black', 'White', 'Blue', 'Green', 'Red', 'Gold', 'Silver', 'Grey', 'Purple', 'Pink'];
const RAM_OPTIONS = ['2 GB', '3 GB', '4 GB', '6 GB', '8 GB', '12 GB', '16 GB'];
const STORAGE_OPTIONS = ['32 GB', '64 GB', '128 GB', '256 GB', '512 GB', '1 TB'];

/**
 * Create or edit one product.
 *
 * <p>The name is optional: left blank it is built from brand, model, colour and
 * memory, which is what the counter will search for anyway. Everything price-related
 * here is the current price - each purchase batch keeps what that consignment cost
 * and sells for.
 */
const ProductForm = ({ product, onClose }: { product: Product | null; onClose: (created?: Product) => void }) => {
  const { openSnackbar } = useSnackbar();
  const [saveProduct, { isLoading: saving }] = useSaveProductMutation();
  const [updateProduct, { isLoading: updating }] = useUpdateProductMutation();
  const [findByBarcode] = useLazyFindProductByBarcodeQuery();
  const [checkDuplicate] = useCheckProductDuplicateMutation();

  /*
   * Shown under the name box, not in a snackbar. The backend has always refused a
   * duplicate name, but it said so in a message floating at the edge of the screen,
   * which does not tell anyone which box to correct.
   */
  const [nameError, setNameError] = useState('');

  const checkName = async (value: string) => {
    const name = String(value ?? '').trim();
    setNameError('');
    if (!name) return;
    try {
      const answer = await checkDuplicate({ id: product?.id, productName: name }).unwrap();
      if (answer?.duplicate) {
        setNameError('A product with this name already exists');
      }
    } catch {
      // Could not ask - let the save decide rather than block on a network hiccup.
    }
  };

  /*
   * Checked against the shop's books as soon as the box is left, and shown under the
   * field rather than in a snackbar: a message that floats at the edge of the screen
   * does not tell anyone WHICH box to correct, which is what the tester asked for.
   */
  const [barcodeError, setBarcodeError] = useState('');

  const checkBarcode = async (value: string) => {
    const barcode = String(value ?? '').trim();
    setBarcodeError('');
    if (!barcode) return;
    try {
      const found = await findByBarcode(barcode).unwrap();
      // Its own barcode is not a clash: this runs on edit as well as on create.
      if (found && found.id !== product?.id) {
        setBarcodeError(`Already used by ${found.productName}`);
      }
    } catch {
      // Nothing has this barcode. That is the normal case and not an error.
    }
  };

  const [form, setForm] = useState<Product>(
    product ?? { serialTracked: true, uom: 'PCS', specs: [] },
  );
  const [specs, setSpecs] = useState<ProductSpec[]>(product?.specs ?? []);
  const [addMaster, setAddMaster] = useState<'BRAND' | 'MODEL' | null>(null);
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const { data: brands = [], refetch: refetchBrands } = useGetBrandsQuery();
  const brandId = Number(form.brandId) || 0;
  const { data: models = [], refetch: refetchModels } = useGetModelsByBrandQuery(brandId, { skip: !brandId });

  useEffect(() => {
    if (product) {
      setForm(product);
      setSpecs(product.specs ?? []);
    }
  }, [product]);

  const set = (key: keyof Product, value: unknown) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: false }));
  };

  // Switching an existing product between serial-tracked and counted would orphan whatever
  // is already in stock, so it is fixed once anything has been bought.
  const lockedTracking = !!product?.id && Number(product.availableStock ?? 0) > 0;

  const selectedBrand = brands.find((brand) => brand.id === form.brandId) ?? null;
  const selectedModel = models.find((model) => model.id === form.modelId) ?? null;

  const save = async () => {
    // A name is only required when there is nothing to build one from.
    const needsName = !form.brandId && !form.modelId && !String(form.productName ?? '').trim();
    if (needsName) {
      setErrors({ productName: true });
      openSnackbar('Give the product a name, or pick a brand and model', 'warning');
      return;
    }

    /*
     * The same specification twice is always a mistake - a product cannot have two
     * different display sizes, and two rows saying the same thing just make the filters
     * on the product screen list it twice.
     *
     * Compared on the name alone, not name and value together: "Display 6.1" and
     * "Display 6.5" on one product is a contradiction, not two facts.
     */
    const named = specs.filter((spec) => spec.specName?.trim());
    const seen = new Set<string>();
    const repeated = named.find((spec) => {
      const key = spec.specName!.trim().toLowerCase();
      if (seen.has(key)) return true;
      seen.add(key);
      return false;
    });
    if (repeated) {
      openSnackbar(`"${repeated.specName!.trim()}" is listed more than once. Keep one of them.`, 'warning');
      return;
    }

    /*
     * Re-checked here, not just on blur: pressing Save straight from the barcode box
     * never fires a blur in some browsers, and a stale pass from an earlier value
     * would let the clash through.
     */
    const typedName = String(form.productName ?? '').trim();
    if (typedName) {
      try {
        const answer = await checkDuplicate({ id: product?.id, productName: typedName }).unwrap();
        if (answer?.duplicate) {
          setNameError('A product with this name already exists');
          return;
        }
      } catch {
        // free
      }
    }

    const barcode = String(form.barcode ?? '').trim();
    if (barcode) {
      try {
        const clash = await findByBarcode(barcode).unwrap();
        if (clash && clash.id !== product?.id) {
          setBarcodeError(`Already used by ${clash.productName}`);
          return;
        }
      } catch {
        // free
      }
    }

    const payload: Product = {
      ...form,
      specs: named,
    };

    try {
      if (product?.id) {
        await updateProduct({ ...payload, id: product.id }).unwrap();
        openSnackbar('Product updated', 'success');
        onClose();
      } else {
        const saved = await saveProduct(payload).unwrap();
        openSnackbar(`${saved.productName} created`, 'success');
        // Handed back, so a screen that opened this to create something mid-entry -
        // a purchase line, a bill line - can put it straight on the line.
        onClose(saved);
      }
    } catch (error: any) {
      /*
       * The backend guards the barcode too, and if it is the one that refused, the
       * message belongs under the box rather than only in a snackbar - the last line
       * of defence should still point at the field it is talking about.
       */
      const message = String(error?.data?.message || '');
      if (/barcode/i.test(message)) {
        setBarcodeError(message);
        return;
      }
      if (/already exists/i.test(message)) {
        setNameError(message);
        return;
      }
      openSnackbar(message || 'Could not save the product', 'error');
    }
  };

  return (
    <Card sx={{ p: 3, position: 'relative' }}>
      <IconButton sx={{ position: 'absolute', right: 12, top: 12 }} onClick={() => onClose()} aria-label="Close">
        <CloseRoundedIcon />
      </IconButton>

      <Typography variant="h5" sx={{ mb: 0.5 }}>
        {product ? 'EDIT PRODUCT' : 'NEW PRODUCT'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Handsets are followed one serial number at a time. Everything else is counted by quantity.
      </Typography>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} color="primary.main">
          How is this counted in stock?
        </Typography>
        <Typography variant="caption" color="text.secondary">
          It decides what the purchase screen asks for: one serial number per piece, or just a quantity.
        </Typography>
        <RadioGroup
          row
          value={form.serialTracked === false ? 'QUANTITY' : 'SERIAL'}
          onChange={(event) => set('serialTracked', event.target.value === 'SERIAL')}
        >
          <FormControlLabel
            value="SERIAL"
            disabled={lockedTracking}
            control={<Radio />}
            label="With serial number (handsets, watches, anything with an IMEI)"
          />
          <FormControlLabel
            value="QUANTITY"
            disabled={lockedTracking}
            control={<Radio />}
            label="Without serial number (covers, chargers, cables)"
          />
        </RadioGroup>
        {lockedTracking && (
          <Typography variant="caption" color="warning.main">
            This cannot change while there is stock: the pieces already in hand are counted the way it is set now.
          </Typography>
        )}
      </Box>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={8}>
          {/* Required only when there is no brand and model to build a name from, so the
              asterisk appears and disappears with that - marking it always required would
              be a lie, and marking it never required is what the tester tripped over. */}
          <TextField
            label="Product Name"
            fullWidth
            required={!form.brandId && !form.modelId}
            error={errors.productName || !!nameError}
            helperText={
              nameError
                || (errors.productName
                  ? 'Give the product a name, or pick a brand and model'
                  : 'Leave empty and it is built from brand, model, colour and memory')
            }
            value={form.productName ?? ''}
            onChange={(event) => {
              set('productName', event.target.value);
              if (nameError) setNameError('');
            }}
            onBlur={(event) => checkName(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Barcode"
            fullWidth
            value={form.barcode ?? ''}
            error={!!barcodeError}
            helperText={barcodeError || 'Scan or type it. Must not already be in use'}
            onChange={(event) => {
              set('barcode', event.target.value);
              if (barcodeError) setBarcodeError('');
            }}
            onBlur={(event) => checkBarcode(event.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
          <Autocomplete
            fullWidth
            options={brands}
            value={selectedBrand}
            getOptionLabel={(option) => option.brandName ?? ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, value) => {
              set('brandId', value?.id ?? null);
              set('modelId', null);
            }}
            renderInput={(params) => <TextField {...params} label="Brand" />}
          />
          <IconButton sx={{ ml: 1 }} tabIndex={-1} color="primary" onClick={() => setAddMaster('BRAND')} aria-label="New brand">
            <AddCircleOutlineIcon />
          </IconButton>
        </Grid>

        <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
          <Autocomplete
            fullWidth
            options={models}
            value={selectedModel}
            disabled={!brandId}
            getOptionLabel={(option) => option.modelName ?? ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, value) => set('modelId', value?.id ?? null)}
            renderInput={(params) => <TextField {...params} label="Model" helperText={!brandId ? 'Choose a brand first' : ''} />}
          />
          <IconButton
            sx={{ ml: 1 }}
            tabIndex={-1}
            color="primary"
            disabled={!brandId}
            onClick={() => setAddMaster('MODEL')}
            aria-label="New model"
          >
            <AddCircleOutlineIcon />
          </IconButton>
        </Grid>

        <Grid item xs={12} md={4}>
          <Autocomplete
            freeSolo
            options={COLOURS}
            value={form.colour ?? ''}
            onInputChange={(_event, value) => set('colour', value)}
            renderInput={(params) => <TextField {...params} label="Colour" />}
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <Autocomplete
            freeSolo
            options={RAM_OPTIONS}
            value={form.ram ?? ''}
            onInputChange={(_event, value) => set('ram', value)}
            renderInput={(params) => <TextField {...params} label="RAM" />}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Autocomplete
            freeSolo
            options={STORAGE_OPTIONS}
            value={form.storage ?? ''}
            onInputChange={(_event, value) => set('storage', value)}
            renderInput={(params) => <TextField {...params} label="Storage" />}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField select label="UOM" fullWidth value={form.uom ?? 'PCS'} onChange={(event) => set('uom', event.target.value)}>
            {UOMS.map((uom) => (
              <MenuItem key={uom} value={uom}>
                {uom}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField label="HSN Code" fullWidth value={form.hsnCode ?? ''} onChange={(event) => set('hsnCode', event.target.value)} />
        </Grid>

        <Grid item xs={6} md={4}>
          <TextField
            label="MRP"
            type="number"
            fullWidth
            value={form.mrp ?? ''}
            onChange={(event) => set('mrp', event.target.value === '' ? null : Number(event.target.value))}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <TextField
            label="Selling Price"
            type="number"
            fullWidth
            helperText="A purchase batch can override this"
            value={form.sellingPrice ?? ''}
            onChange={(event) => set('sellingPrice', event.target.value === '' ? null : Number(event.target.value))}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <TextField
            label="GST %"
            type="number"
            fullWidth
            helperText="Left blank, the shop default is used"
            value={form.gstPercentage ?? ''}
            onChange={(event) => set('gstPercentage', event.target.value === '' ? null : Number(event.target.value))}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <TextField
            label="Reorder Level"
            type="number"
            fullWidth
            helperText="Low Stock warns me at this many or fewer"
            value={form.reorderLevel ?? ''}
            onChange={(event) => set('reorderLevel', event.target.value === '' ? null : Number(event.target.value))}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Description"
            fullWidth
            value={form.description ?? ''}
            onChange={(event) => set('description', event.target.value)}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Other specifications
          </Typography>
          <Typography variant="caption" color="text.secondary">
            5G, dual SIM, display size - anything beyond colour, RAM and storage.
          </Typography>
        </Box>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setSpecs([...specs, { specName: '', specValue: '' }])}>
          Add
        </Button>
      </Stack>

      {specs.map((spec, index) => (
        <Stack key={spec.id ?? `new-${index}`} direction="row" gap={1} sx={{ mb: 1 }}>
          <TextField
            label="Name"
            value={spec.specName}
            onChange={(event) =>
              setSpecs((previous) => previous.map((row, i) => (i === index ? { ...row, specName: event.target.value } : row)))
            }
          />
          <TextField
            label="Value"
            value={spec.specValue ?? ''}
            onChange={(event) =>
              setSpecs((previous) => previous.map((row, i) => (i === index ? { ...row, specValue: event.target.value } : row)))
            }
          />
          <IconButton
            size="small"
            color="error"
            onClick={() => setSpecs((previous) => previous.filter((_row, i) => i !== index))}
            aria-label="Remove specification"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}

      <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mt: 3 }}>
        <Button variant="outlined" color="inherit" onClick={() => onClose()}>
          Cancel
        </Button>
        <LoadingButton variant="contained" loading={saving || updating} onClick={save}>
          {product ? 'Update' : 'Save Product'}
        </LoadingButton>
      </Stack>

      {addMaster && (
        <MasterDrawer
          tab={addMaster === 'BRAND' ? 'BRAND' : 'MODEL'}
          editData={addMaster === 'MODEL' ? ({ brandId: form.brandId } as any) : null}
          closeDrawer={() => {
            setAddMaster(null);
            // A brand or model created here should be selectable straight away.
            refetchBrands();
            if (brandId) refetchModels();
          }}
        />
      )}
    </Card>
  );
};

export default ProductForm;
